import { useState } from 'react';
import { ChevronLeft, X } from 'lucide-react';

import type { Season } from '@/types/season';
import { ExpandChevron } from '@/components/common/ExpandChevron';
import { deriveSeasonNameDisplay } from '@/utils/season-utils';

interface PrepHeaderProps {
  season: Season;
  seasons: Season[];
  totalCount: number;
  completedCount: number;
  onBack: () => void;
  onSeasonSelect: (id: string) => void;
}

const TICK_BAR_MAX = 60;

/**
 * PrepHeader — sticky instrument bar for the full-page task list (DASH-TD1 / M100).
 *
 * Season switcher: title is `deriveSeasonNameDisplay(season).title` verbatim + ExpandChevron.
 * Sub-label: `STATE · METHOD · YEAR` beneath the title.
 * No seasonTypeLabel, zoneLabel, or metaRight — removed per spec §1.
 * onBack is preserved (navigate handler).
 */
export function PrepHeader({
  season,
  seasons,
  totalCount,
  completedCount,
  onBack,
  onSeasonSelect,
}: PrepHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const useTicks = totalCount > 0 && totalCount <= TICK_BAR_MAX;
  const progressFraction = totalCount > 0 ? completedCount / totalCount : 0;

  const { title, subLabel } = deriveSeasonNameDisplay(season);
  const isMulti = seasons.length > 1;

  function handleSeasonSelect(id: string) {
    onSeasonSelect(id);
    setMenuOpen(false);
  }

  return (
    <>
      <div
        className="sticky top-0 z-10 border-b-[0.5px] border-[var(--color-border)]/60"
        style={{ backgroundColor: 'rgba(28,26,23,0.92)', backdropFilter: 'blur(10px)' }}
        data-testid="prep-header"
      >
        {/* Row 1: back chevron + season title (switcher) */}
        <div className="flex items-center gap-2 px-4 pt-4 pb-1">
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            data-testid="prep-header-back"
            className="flex-shrink-0 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
          >
            <ChevronLeft size={18} aria-hidden="true" />
          </button>

          {/* Season switcher button */}
          {isMulti ? (
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen}
              aria-label="Switch season"
              data-testid="prep-header-season-switcher"
              className="flex-1 min-w-0 flex items-center gap-1.5 text-left"
            >
              <h1
                className="min-w-0 font-display text-[20px] text-[var(--color-text-display)] truncate"
                style={{ letterSpacing: '-0.2px' }}
                data-testid="prep-header-title"
              >
                {title}
              </h1>
              <ExpandChevron
                expanded={menuOpen}
                size={14}
                className="flex-shrink-0 text-[var(--color-text-secondary)]"
              />
            </button>
          ) : (
            <div className="flex-1 min-w-0 flex items-center gap-1.5">
              <h1
                className="min-w-0 font-display text-[20px] text-[var(--color-text-display)] truncate"
                style={{ letterSpacing: '-0.2px' }}
                data-testid="prep-header-title"
              >
                {title}
              </h1>
            </div>
          )}
        </div>

        {/* Row 1b: sub-label under the title */}
        <div className="px-4 pb-1 pl-[calc(1rem+18px+0.5rem)]">
          <span
            className="field-label text-[var(--color-text-muted)]"
            data-testid="prep-header-sub-label"
          >
            {subLabel}
          </span>
        </div>

        {/* Row 2: fraction left */}
        <div className="flex items-baseline justify-between px-4 pb-2">
          <div className="flex items-baseline gap-1">
            <span
              className="font-display text-[var(--color-text-display)]"
              style={{ fontSize: '28px', fontVariantNumeric: 'tabular-nums' }}
              data-testid="prep-header-completed-count"
            >
              {completedCount}
            </span>
            <span className="text-[13px] text-[var(--color-text-muted)]">
              / {totalCount}
            </span>
            <span className="field-label ml-1">tasks</span>
          </div>
        </div>

        {/* Row 3: tick bar */}
        {totalCount > 0 && (
          <div className="px-4 pb-3" data-testid="prep-header-tick-bar">
            {useTicks ? (
              <div className="flex" style={{ gap: '1.5px' }}>
                {Array.from({ length: totalCount }, (_, i) => {
                  const isFilled = i < completedCount;
                  return (
                    <div
                      key={i}
                      data-tick-index={i}
                      data-tick-state={isFilled ? 'filled' : 'empty'}
                      style={{
                        flex: 1,
                        height: '2px',
                        backgroundColor: isFilled
                          ? 'var(--color-accent)'
                          : 'var(--color-border)',
                        opacity: isFilled ? 1 : 0.7,
                      }}
                    />
                  );
                })}
              </div>
            ) : (
              <div
                className="relative overflow-hidden"
                style={{ height: '2px', backgroundColor: 'var(--color-border)', opacity: 0.7 }}
                data-testid="prep-header-tick-bar-single"
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: `${progressFraction * 100}%`,
                    backgroundColor: 'var(--color-accent)',
                    opacity: 1,
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Season switcher menu (backdrop + list) */}
      {menuOpen && isMulti && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/60"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
            data-testid="prep-header-menu-backdrop"
          />

          {/* Bottom sheet */}
          <div
            className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--color-elevated)] border-t border-[var(--color-border)] rounded-t-2xl shadow-card-lg flex flex-col max-h-[70vh]"
            role="dialog"
            aria-modal="true"
            aria-label="Select season"
            data-testid="prep-header-season-menu"
          >
            {/* Sheet header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h2 className="font-display text-[18px] text-[var(--color-text-display)]">
                Select Season
              </h2>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Close season picker"
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
                data-testid="prep-header-menu-close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Season list */}
            <div className="overflow-y-auto pb-8" data-testid="prep-header-menu-list">
              {seasons.map((s) => {
                const isSelected = s.id === season.id;
                const { title: sTitle, subLabel: sSubLabel } = deriveSeasonNameDisplay(s);

                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => handleSeasonSelect(s.id)}
                    data-testid={`prep-header-season-option-${s.id}`}
                    aria-pressed={isSelected}
                    className="w-full flex items-center justify-between px-5 py-3.5 border-b border-[var(--color-border)] text-left hover:bg-[var(--color-surface)] transition-colors duration-100"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span
                        className={`text-body-sm ${isSelected ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]'}`}
                        data-testid={`prep-header-season-option-title-${s.id}`}
                      >
                        {sTitle}
                      </span>
                      <span className="field-label text-[var(--color-text-muted)]">
                        {sSubLabel}
                      </span>
                    </div>
                    {isSelected && (
                      <span
                        className="text-[10px] field-label text-[var(--color-accent)]"
                        aria-hidden="true"
                      >
                        ACTIVE
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
